import type { Mastra } from '@mastra/core/mastra';
import type { RequestContext } from '@mastra/core/request-context';
import { formatZodError } from '@mastra/server/handlers/error';
import { isZodError, SERVER_ROUTES } from '@mastra/server/server-adapter';
import type { ServerRoute, ServerContext, ZodErrorLike } from '@mastra/server/server-adapter';
import { BadRequestException, Inject, Injectable, Logger } from '@nestjs/common';

import { MASTRA, MASTRA_OPTIONS } from '../constants';
import type { MastraModuleOptions } from '../mastra.module';

export interface RouteMatch {
  route: ServerRoute;
  pathParams: Record<string, string>;
}

export interface RouteHandlerParams {
  /** URL path parameters (e.g., { agentId: '123' }) */
  pathParams: Record<string, string>;
  /** Query string parameters */
  queryParams: Record<string, unknown>;
  /** Request body (for POST/PUT/PATCH) */
  body: unknown;
  /** Request context (user, session, etc.) */
  requestContext: RequestContext;
  /** Abort signal for request cancellation */
  abortSignal: AbortSignal;
  /** The web-standard Request object for accessing headers, cookies, etc. */
  request?: Request;
}

export interface RouteHandlerResult {
  /** The result data from the handler */
  data: unknown;
  /** Response type determines how to send the response */
  responseType: 'json' | 'stream' | 'datastream-response' | 'mcp-http' | 'mcp-sse';
  /** Stream format (only for 'stream' responseType) */
  streamFormat?: 'sse' | 'stream';
  /** Whether to flush an SSE comment on connect before stream data arrives */
  sseFlushOnConnect?: boolean;
}

type ValidationErrorContext = 'query' | 'body' | 'path';

const VALIDATION_CONTEXT_LABELS: Record<ValidationErrorContext, string> = {
  query: 'query parameters',
  body: 'request body',
  path: 'path parameters',
};

function getSchemaTypeName(schema: unknown): string | undefined {
  if (!schema || typeof schema !== 'object') {
    return undefined;
  }

  const definition = (schema as any)._def ?? (schema as any).def;
  return definition?.typeName ?? definition?.type;
}

function unwrapOptionalNullable(schema: any): any {
  let inner = schema;
  let typeName = getSchemaTypeName(inner);

  while (
    typeName === 'ZodOptional' ||
    typeName === 'optional' ||
    typeName === 'ZodNullable' ||
    typeName === 'nullable'
  ) {
    const definition = inner?._def ?? inner?.def;
    if (!definition?.innerType) {
      return inner;
    }
    inner = definition.innerType;
    typeName = getSchemaTypeName(inner);
  }

  return inner;
}

/**
 * Service that bridges NestJS controllers to Mastra route handlers.
 * Handles parameter validation and invokes the appropriate handler.
 */
@Injectable()
export class RouteHandlerService {
  private readonly logger = new Logger(RouteHandlerService.name);
  private readonly routeMap: Map<string, ServerRoute>;
  private readonly reservedParamKeys = new Set([
    'mastra',
    'requestContext',
    'registeredTools',
    'taskStore',
    'abortSignal',
    'routePrefix',
  ]);

  constructor(
    @Inject(MASTRA) private readonly mastra: Mastra,
    @Inject(MASTRA_OPTIONS) private readonly options: MastraModuleOptions,
  ) {
    // Build a map of path+method to route for fast lookup
    this.routeMap = new Map();
    for (const route of SERVER_ROUTES) {
      this.registerRoute(route);
    }
  }

  /**
   * Register a route with the catch-all NestJS controller.
   */
  registerRoute(route: ServerRoute): void {
    const key = this.getRouteKey(route.method, route.path);
    this.routeMap.set(key, route);
  }

  /**
   * Find a route by exact method and path pattern.
   * Use matchRoute() for parameterized path matching.
   */
  findRoute(method: string, path: string): ServerRoute | undefined {
    const key = this.getRouteKey(method, path);
    return this.routeMap.get(key);
  }

  /**
   * Match a request method and path against registered routes.
   * Handles both exact matches and parameterized path patterns.
   *
   * @param method - HTTP method (GET, POST, etc.)
   * @param path - Request path to match
   * @returns Route match with path parameters, or null if no match
   */
  matchRoute(method: string, path: string): RouteMatch | null {
    // Handle ALL method routes (OPTIONS preflight)
    const methodsToCheck = method === 'OPTIONS' ? ['ALL', method] : [method, 'ALL'];

    for (const checkMethod of methodsToCheck) {
      // First check exact match (fast path)
      const exactKey = this.getRouteKey(checkMethod, path);
      const exactRoute = this.routeMap.get(exactKey);
      if (exactRoute) {
        return { route: exactRoute, pathParams: {} };
      }

      // Then check parameterized routes
      for (const route of this.routeMap.values()) {
        if (route.method.toUpperCase() !== checkMethod) {
          continue;
        }

        const pathParams = this.matchPath(route.path, path);
        if (pathParams) {
          return { route, pathParams };
        }
      }
    }

    return null;
  }

  /**
   * Get all routes (for dynamic controller generation).
   */
  getAllRoutes(): readonly ServerRoute[] {
    return Array.from(this.routeMap.values());
  }

  /**
   * Match a path against a route pattern.
   * Returns path parameters if matched, null otherwise.
   */
  private matchPath(pattern: string, path: string): Record<string, string> | null {
    const patternParts = pattern.split('/').filter(Boolean);
    const pathParts = path.split('/').filter(Boolean);

    // Must have same number of parts
    if (patternParts.length !== pathParts.length) {
      return null;
    }

    const params: Record<string, string> = {};

    for (let i = 0; i < patternParts.length; i++) {
      const patternPart = patternParts[i];
      const pathPart = pathParts[i];

      // These should always be defined since we checked array lengths match
      if (patternPart === undefined || pathPart === undefined) {
        return null;
      }

      if (patternPart.startsWith(':')) {
        // This is a parameter - decode with error handling
        const paramName = patternPart.slice(1);
        try {
          params[paramName] = decodeURIComponent(pathPart);
        } catch {
          // Invalid percent encoding - throw a proper 400 error
          throw new BadRequestException(`Invalid URL encoding in path parameter: ${paramName}`);
        }
      } else if (patternPart !== pathPart) {
        // Literal parts must match exactly
        return null;
      }
    }

    return params;
  }

  /**
   * Execute a route handler with the given parameters.
   */
  async executeHandler(route: ServerRoute, params: RouteHandlerParams): Promise<RouteHandlerResult> {
    let validatedPathParams = params.pathParams;
    if (route.pathParamSchema) {
      try {
        validatedPathParams = (await route.pathParamSchema.parseAsync(params.pathParams)) as Record<string, string>;
      } catch (error) {
        if (isZodError(error)) {
          throw this.createValidationError(route, error, 'path');
        }
        throw error;
      }
    }

    let validatedQueryParams = params.queryParams;
    if (route.queryParamSchema) {
      try {
        validatedQueryParams = (await route.queryParamSchema.parseAsync(params.queryParams)) as Record<string, unknown>;
      } catch (error) {
        if (isZodError(error)) {
          throw this.createValidationError(route, error, 'query');
        }
        throw error;
      }
    }

    let validatedBody = params.body;
    if (route.bodySchema) {
      try {
        validatedBody = await this.parseBody(route, params.body);
      } catch (error) {
        if (isZodError(error)) {
          throw this.createValidationError(route, error, 'body');
        }
        throw error;
      }
    }

    const context: ServerContext = {
      mastra: this.mastra,
      requestContext: params.requestContext,
      registeredTools: this.options.tools,
      taskStore: this.options.taskStore,
      abortSignal: params.abortSignal,
      routePrefix: this.options.prefix,
      request: params.request,
    };

    const handlerParams = {
      ...this.omitReservedKeys(validatedPathParams),
      ...this.omitReservedKeys(validatedQueryParams),
      ...(typeof validatedBody === 'object' && validatedBody !== null ? this.omitReservedKeys(validatedBody) : {}),
      ...context,
    };

    const data = await route.handler(handlerParams);

    return {
      data,
      responseType: route.responseType,
      streamFormat: route.streamFormat,
      sseFlushOnConnect: route.sseFlushOnConnect,
    };
  }

  private async parseBody(route: ServerRoute, body: unknown): Promise<unknown> {
    const bodySchema = route.bodySchema;
    if (!bodySchema) {
      return body;
    }

    if (body === undefined) {
      const omitted = await bodySchema.safeParseAsync(undefined);
      if (omitted.success) {
        return omitted.data;
      }

      const schemaType = getSchemaTypeName(unwrapOptionalNullable(bodySchema));
      if (schemaType === 'object' || schemaType === 'ZodObject') {
        const emptyObject = await bodySchema.safeParseAsync({});
        if (emptyObject.success) {
          return emptyObject.data;
        }
      }

      throw omitted.error;
    }

    return bodySchema.parseAsync(body);
  }

  private createValidationError(
    route: ServerRoute,
    error: ZodErrorLike,
    context: ValidationErrorContext,
  ): ValidationError {
    const hook = route.onValidationError ?? this.mastra.getServer()?.onValidationError;

    if (hook) {
      try {
        const result = hook(error as any, context);
        if (result) {
          return new ValidationError(
            `Invalid ${VALIDATION_CONTEXT_LABELS[context]}`,
            error,
            result.status,
            result.body,
          );
        }
      } catch (hookError) {
        this.logger.error('Error in custom onValidationError hook', hookError);
      }
    }

    return new ValidationError(
      `Invalid ${VALIDATION_CONTEXT_LABELS[context]}`,
      error,
      400,
      formatZodError(error, VALIDATION_CONTEXT_LABELS[context]),
    );
  }

  private getRouteKey(method: string, path: string): string {
    return `${method.toUpperCase()}:${path}`;
  }

  private omitReservedKeys(value: unknown): Record<string, unknown> {
    if (!value || typeof value !== 'object') {
      return {};
    }

    const result: Record<string, unknown> = {};
    for (const [key, entryValue] of Object.entries(value)) {
      if (this.reservedParamKeys.has(key)) {
        this.logger.warn(`Ignoring reserved handler parameter key "${key}" from request input`);
        continue;
      }
      result[key] = entryValue;
    }

    return result;
  }
}

/**
 * Error class for validation failures with Zod error details.
 *
 * `zodError` is typed as `ZodErrorLike` (a structural subset of `ZodError`
 * exposing `issues[]`) so that consumers pinning a different `zod` major than
 * the one bundled with this adapter still type-check. The runtime value is
 * the actual `ZodError` thrown by the route schema and supports all of its
 * methods at runtime — cast to your installed `ZodError` if you need them.
 */
export class ValidationError extends Error {
  constructor(
    message: string,
    public readonly zodError: ZodErrorLike,
    public readonly status = 400,
    public readonly body: unknown = formatZodError(zodError, 'request'),
  ) {
    super(message);
    this.name = 'ValidationError';
  }
}
